<template>
  <v-dialog
    eager
    v-model="dialog"
    persistent
    hide-overlay
    :transition="''"
    max-width="400"
  >
    <div :class="dialogClass">
      <v-card
        ref="card"
        class="card-collapsable"
        :class="{
          'is-not-collapsed': !isCollapsed,
          'is-collapsed': isCollapsed,
          'animation-shake': isCollapsed,
          isMobile: is_mobile,
        }"
        :style="collapsedStyle"
      >
        <div
          class="header touchable"
          :class="headerClass"
          role="tab"
          @pointerdown="startCollapsedDrag"
          @click.prevent="toggle"
        >
          <div class="always-visible d-flex justify-center align-center">
            <slot name="visible"></slot>
          </div>
          <div class="title">
            <slot name="title"></slot>
          </div>
          <v-btn fab small depressed class="icon-collapse mr-2">
            <v-icon style="transform: scaleX(-1)">pin_invoke</v-icon>
          </v-btn>

          <div class="overlay"></div>
        </div>

        <div class="body">
          <slot name="body"></slot>
        </div>

        <div class="actions">
          <v-divider></v-divider>
          <slot name="actions"></slot>
        </div>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
import Vuex from "vuex";

const POSITION_STORAGE_PREFIX = "card-collapsable-position";
const DRAG_THRESHOLD = 4;

export default {
  name: "CardCollapsable",

  data() {
    return {
      isCollapsed: false,
      collapsedPosition: null,
      dragState: null,
      ignoreNextClick: false,
    };
  },

  props: {
    headerClass: {
      type: [String, Object],
      required: true,
      default: "",
    },
    dialogClass: {
      type: String,
      required: true,
      default: "",
    },
    dialogProp: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  watch: {
    isCollapsed(val) {
      if (val) {
        this.restoreCollapsedPosition();
        this.$nextTick(this.clampCollapsedPosition);
      } else {
        this.stopCollapsedDrag();
      }
      this.$emit("changeCollapse", val);
    },
    dialog(val) {
      if (val) {
        this.isCollapsed = false;
      }
    },
    is_mobile() {
      if (this.isCollapsed) {
        this.restoreCollapsedPosition();
        this.$nextTick(this.clampCollapsedPosition);
      }
    },
    isLayoutPanoramica() {
      if (this.isCollapsed) {
        this.restoreCollapsedPosition();
        this.$nextTick(this.clampCollapsedPosition);
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.clampCollapsedPosition);
  },
  destroyed() {
    this.stopCollapsedDrag();
    window.removeEventListener("resize", this.clampCollapsedPosition);
  },
  computed: {
    dialog() {
      return this.dialogProp;
    },
    collapsedStorageKey() {
      return [
        POSITION_STORAGE_PREFIX,
        this.is_mobile ? "mobile" : "desktop",
        this.isLayoutPanoramica ? "panoramica-open" : "panoramica-closed",
      ].join(":");
    },
    defaultCollapsedPosition() {
      return this.is_mobile
        ? { left: 5, top: 60 }
        : { left: Math.round(window.innerWidth * 0.26), top: 70 };
    },
    collapsedStyle() {
      if (!this.isCollapsed || !this.collapsedPosition) {
        return {};
      }

      return {
        left: this.collapsedPosition.left + "px",
        top: this.collapsedPosition.top + "px",
      };
    },
    ...Vuex.mapGetters(["is_mobile", "isLayoutPanoramica"]),
  },

  methods: {
    toggle() {
      if (this.ignoreNextClick) {
        this.ignoreNextClick = false;
        return;
      }

      if (this.dragState && this.dragState.dragged) {
        return;
      }

      this.isCollapsed = !this.isCollapsed;
    },
    restoreCollapsedPosition() {
      const savedPosition = this.getSavedCollapsedPosition();
      this.collapsedPosition = savedPosition || this.defaultCollapsedPosition;
    },
    getSavedCollapsedPosition() {
      try {
        const rawPosition = window.localStorage.getItem(this.collapsedStorageKey);
        if (!rawPosition) {
          return null;
        }

        const position = JSON.parse(rawPosition);
        if (
          typeof position.left !== "number" ||
          typeof position.top !== "number"
        ) {
          return null;
        }

        return position;
      } catch (error) {
        return null;
      }
    },
    saveCollapsedPosition() {
      if (!this.collapsedPosition) {
        return;
      }

      try {
        window.localStorage.setItem(
          this.collapsedStorageKey,
          JSON.stringify(this.collapsedPosition)
        );
      } catch (error) {
        // localStorage non disponibile: il drag resta valido fino a refresh.
      }
    },
    startCollapsedDrag(event) {
      if (!this.isCollapsed || (event.button !== undefined && event.button > 0)) {
        return;
      }

      const rect = this.$refs.card.$el.getBoundingClientRect();
      const startPosition = this.collapsedPosition || {
        left: rect.left,
        top: rect.top,
      };

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        left: startPosition.left,
        top: startPosition.top,
        dragged: false,
      };

      if (event.currentTarget.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      window.addEventListener("pointermove", this.dragCollapsed);
      window.addEventListener("pointerup", this.endCollapsedDrag);
      window.addEventListener("pointercancel", this.endCollapsedDrag);
    },
    dragCollapsed(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) {
        return;
      }

      const deltaX = event.clientX - this.dragState.startX;
      const deltaY = event.clientY - this.dragState.startY;

      if (
        Math.abs(deltaX) > DRAG_THRESHOLD ||
        Math.abs(deltaY) > DRAG_THRESHOLD
      ) {
        this.dragState.dragged = true;
      }

      this.collapsedPosition = this.clampPosition({
        left: event.clientX - this.dragState.offsetX,
        top: event.clientY - this.dragState.offsetY,
      });
    },
    endCollapsedDrag(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) {
        return;
      }

      if (this.dragState.dragged) {
        this.ignoreNextClick = true;
        this.saveCollapsedPosition();
      }

      window.removeEventListener("pointermove", this.dragCollapsed);
      window.removeEventListener("pointerup", this.endCollapsedDrag);
      window.removeEventListener("pointercancel", this.endCollapsedDrag);

      setTimeout(() => {
        this.dragState = null;
      }, 0);
    },
    stopCollapsedDrag() {
      this.dragState = null;
      window.removeEventListener("pointermove", this.dragCollapsed);
      window.removeEventListener("pointerup", this.endCollapsedDrag);
      window.removeEventListener("pointercancel", this.endCollapsedDrag);
    },
    clampCollapsedPosition() {
      if (!this.isCollapsed || !this.collapsedPosition) {
        return;
      }

      this.collapsedPosition = this.clampPosition(this.collapsedPosition);
      this.saveCollapsedPosition();
    },
    clampPosition(position) {
      const cardEl = this.$refs.card ? this.$refs.card.$el : null;
      const rect = cardEl ? cardEl.getBoundingClientRect() : null;
      const width = rect ? rect.width : 58;
      const height = rect ? rect.height : 58;
      const margin = 5;

      return {
        left: Math.min(
          Math.max(position.left, margin),
          Math.max(margin, window.innerWidth - width - margin)
        ),
        top: Math.min(
          Math.max(position.top, margin),
          Math.max(margin, window.innerHeight - height - margin)
        ),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../main.scss";

.card-collapsable {
  .header {
    position: relative;
    display: flex;
    align-items: center;
    padding: 10px;
    justify-content: space-between;
    cursor: pointer;

    .overlay {
      position: absolute;
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
      &:hover {
        background: rgb(0 0 0 / 5%);
      }
    }
  }
  .title {
    padding-left: 10px;
    font-size: $font-size-title;
    line-height: 20px;
    font-weight: normal;
  }

  .icon-collapse {
    background-color: rgba(0, 0, 0, 0.12);
    color: white;
  }

  // servono per sovrascrivere il plugin draggable
  // &.is-not-collapsed {
  //   position: relative !important;
  //   left: 0 !important;
  //   top: 0 !important;
  // }

  &.is-collapsed {
    position: fixed !important;
    border-radius: 50% !important;
    touch-action: none;

    .always-visible {
      min-width: 42px;
      min-height: 42px;
    }
    .header {
      border-radius: 50% !important;
      &::before {
        content: none;
      }
    }
    .overlay {
      border-radius: 50% !important;
    }
    .header .title,
    .icon-collapse,
    .body,
    .actions {
      display: none;
    }
  }
}

</style>
